import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard } from '../downtime/DowntimeCard';
import { PRIORITY_LABEL, STATUS_LABEL, STATUS_TONE, threeWPriority } from './Scene.transform';
import {
  nextDayCrews,
  nextDayPlanOwner,
  nextDayPlanRows,
  nextDayPlanWindow,
  nextDayPriorities,
  type NextDayCrew,
  type NextDayPlanRow,
  type NextDayPriority,
} from './NextDayPlan.transform';

const PILL_SM = 'whitespace-nowrap px-2 py-px text-[10px] font-semibold tracking-[0.05em]';

/** The plan tab — what the room hands the incoming production day (locked
 * comp `meeting.html` B): the register rows the day is planned around beside
 * the plan figures it is measured against (the window as that card's meta)
 * and the two crews it is handed to, owned by the seat named on the chip.
 * Every figure comes from `NextDayPlan.transform.ts`; this file lays them out
 * and nothing else. */
export function NextDayPlan() {
  const { state } = useScenario();
  const span = nextDayPlanWindow(state);
  const rows = nextDayPlanRows(state);
  const crews = nextDayCrews(state);
  const priorities = nextDayPriorities();

  return (
    <Commentable name="NextDayPlan">
      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
        <DowntimeCard title="Priorities" meta="register order" className="px-0 pb-0 pt-3 xl:col-span-8 [&>header]:px-4">
          <div className={cn(CARD_TABLE, 'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4')}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>3W</TH>
                  <TH>Issue</TH>
                  <TH>Who</TH>
                  <TH>Commit</TH>
                </TR>
              </THead>
              <TBody>
                {priorities.map((row) => (
                  <PriorityRow key={row.id} row={row} />
                ))}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        <div className="min-w-0 space-y-3 xl:col-span-4">
          <DowntimeCard title="Budget basis" meta={span.label}>
            <dl className="tabular-nums">
              {rows.map((row) => (
                <PlanRow key={row.id} row={row} />
              ))}
            </dl>
          </DowntimeCard>
          <DowntimeCard title="Handed to" actions={<ActorChip actor="Human">{nextDayPlanOwner()}</ActorChip>}>
            <dl>
              {crews.map((crew) => (
                <CrewRow key={crew.kind} crew={crew} />
              ))}
            </dl>
          </DowntimeCard>
        </div>
      </div>
    </Commentable>
  );
}

/** One plan line as a fact row: the metric, then its plan figure with any
 * per-shift half as the muted qualifier. */
function PlanRow({ row }: { row: NextDayPlanRow }) {
  const [figure, ...qualifier] = row.plan.split(' · ');
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-divider py-1.5 text-[13px] last:border-b-0">
      <dt className="text-ink-secondary">{row.label}</dt>
      <dd className="whitespace-nowrap text-right font-semibold text-ink-primary">
        {figure}
        {qualifier.length ? <span className="text-xs font-normal text-muted"> · {qualifier.join(' · ')}</span> : null}
      </dd>
    </div>
  );
}

/** One crew the plan is handed to. A two-column grid (comp `.facts .f`), not a
 * wrapping flex row, so `6 on shift · 2 seniors` keeps the comp's one line
 * beside the crew at 1470. `Short-staffed` renders only when the roster says
 * so — this seed's two crews are at strength. */
function CrewRow({ crew }: { crew: NextDayCrew }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-divider py-1.5 text-[12px] last:border-b-0">
      <dt className="min-w-0 text-ink-secondary">
        <span className="font-semibold text-ink-primary">{crew.crewLabel}</span> · {crew.windowLabel}
      </dt>
      <dd className="flex items-center gap-2 whitespace-nowrap font-semibold tabular-nums text-ink-primary">
        {crew.headcount} on shift · {crew.seniors} seniors
        {crew.shortStaffed ? (
          <Pill tone="warning" className={PILL_SM}>
            Short-staffed
          </Pill>
        ) : null}
      </dd>
    </div>
  );
}

function PriorityRow({ row }: { row: NextDayPriority }) {
  const priority = threeWPriority(row);
  return (
    <TR>
      <TD className="whitespace-nowrap">
        {priority === 'standard' ? null : (
          <Pill tone={priority === 'critical' ? 'danger' : 'warning'} className={cn(PILL_SM, 'mr-1.5')}>
            {PRIORITY_LABEL[priority]}
          </Pill>
        )}
        <span className="font-mono text-xs text-muted">{row.id}</span>
      </TD>
      <TD className="min-w-[220px] font-semibold !text-ink-primary">{row.issue}</TD>
      <TD>
        <ActorChip actor="Human">{row.who}</ActorChip>
      </TD>
      <TD className="whitespace-nowrap !text-ink-primary">
        {row.commitDate}
        {row.status === 'overdue' || row.status === 'no-date' ? (
          <Pill tone={STATUS_TONE[row.status]} className={cn(PILL_SM, row.commitDate ? 'ml-1.5' : undefined)}>
            {STATUS_LABEL[row.status]}
          </Pill>
        ) : null}
      </TD>
    </TR>
  );
}
